/**
 * The Hilfe screen's static copy, ported verbatim from the design (COL-533):
 * the topic chips and their sub-chips, the FAQ entries, and the surrounding
 * labels. Two sets — a tenant's topics differ from an applicant's.
 *
 * The DE/EN literals here are the FALLBACK tier, not the whole story. Every
 * string is routed through the translation table -- the flat labels by
 * `translateDictionary`, the nested shapes by `translateFaq` /
 * `translateHelpTopics` at the bottom of this file -- so fr/ar/uk/es render
 * from the rows seeded in `20261021190508` and fall back to the English
 * literal only where a row is missing.
 *
 * This module used to say the nested shape was not worth reshaping and that
 * fr/ar/uk/es would fall back to English. That was wrong in the one place it
 * hurts most: Hilfe is where a tenant goes when something is broken.
 */

import type { TranslateFn } from "@/lib/i18n/dictionary";
import type { DashboardV2Tab } from "@/lib/tenant-dashboard-tabs";

/**
 * What a quick fix can offer besides its text: jump to a dashboard tab, or
 * copy a value to the clipboard. `kind`, `tab` and `value` are data, not
 * copy -- only `label` goes through translation.
 *
 * The `copy` arm currently has no instance. Its only user was the
 * broadcasting-fee number, which was a figure from the design export rather
 * than a real one and came out in the 19 Oct QA pass. Kept because it is
 * generic infrastructure, not because something still uses it.
 */
export type QuickFixAction =
  | { kind: "tab"; label: string; tab: DashboardV2Tab }
  | { kind: "copy"; label: string; value: string };

/**
 * "Quick fix to try first": shown instead of the message box once a sub-chip
 * is picked, until the tenant says it did not help ("Report anyway").
 */
export type QuickFix = { text: string; action?: QuickFixAction };

export type HelpTopic = {
  key: string;
  label: string;
  subs: string[];
  placeholder: string;
  hint: string;
  /**
   * Indexes into `subs` that file as `urgent` rather than `medium`.
   *
   * V1's quick-report templates set `urgent` for "heating not working" and
   * "locked out of room" (SettingsSupportTickets.tsx:850, :863), and the
   * admin queue triages on that. Restyling the form must not quietly
   * downgrade those reports, so the mapping travels with the topic.
   *
   * By INDEX, not label: the labels are translated, so matching on them
   * would silently stop working in German.
   *
   * Deliberately NOT including "Water & bathroom": V1's third urgent
   * template is specifically "hot water not working", and this category also
   * covers a dripping tap. Raised on the PR rather than guessed at.
   */
  urgentSubs?: number[];
  /**
   * Quick fixes keyed by sub INDEX, for the same reason `urgentSubs` is: the
   * labels are translated and can change under a selected chip, the index
   * cannot. Only subs that have a fix true for EVERY property carry one --
   * the design's Wi-Fi password, fuse-box location or contract end date are
   * fixtures of one flat and must not ship as copy.
   */
  quickFixes?: Record<number, QuickFix>;
};

/**
 * `id` is a STABLE handle for the translation key, independent of order and
 * of the text itself. Same reasoning as `urgentSubs` above: the q/a strings
 * are translated, so keying on them would silently stop matching in German,
 * and keying on array position would re-point every row the first time an
 * entry is inserted or reordered.
 */
export type FaqEntry = { id: string; q: string; a: string };

export function getTenantHelpTopics(lang: "en" | "de"): HelpTopic[] {
  return lang === "de"
    ? [
        {
          key: "reparatur",
          label: "Reparatur",
          subs: [
            "Heizung",
            "Wasser & Bad",
            "Küche & Geräte",
            "Möbel",
            "Elektrik",
            "Fenster & Türen",
            "Schlüssel & Schlösser",
          ],
          placeholder: "Was ist kaputt und wo genau? Seit wann?",
          hint: "Fotos helfen dem Handwerker.",
          urgentSubs: [0, 6],
          quickFixes: {
            1: {
              text: "Abfluss verstopft? Probier es zuerst mit der Saugglocke. Läuft Wasser aus, direkt melden.",
            },
            2: {
              text: "Gerät einmal aus- und wieder einschalten und das Sieb prüfen. Hilft meistens.",
            },
            4: {
              text: "Sicherung rausgeflogen? Schau in den Sicherungskasten und drück den Schalter wieder hoch.",
            },
          },
        },
        {
          key: "wg",
          label: "Hausordnung & Konflikte",
          subs: ["Lärm", "Hausordnung", "Sicherheit"],
          placeholder: "Was ist passiert? Seit wann?",
          hint: "Vertraulich. Deine Mitbewohner sehen das nicht.",
          quickFixes: {
            0: {
              text: "Am besten zuerst direkt mit deinen Mitbewohnern ansprechen. Ruhezeiten sind 22 bis 7 Uhr. Hört es nicht auf, sind wir da.",
            },
            1: {
              text: "Steht alles in der Hausordnung. Oft reicht ein Hinweis an deine Mitbewohner.",
            },
          },
        },
        {
          key: "internet",
          label: "Internet",
          subs: ["Kein Internet", "Langsam", "Zugangsdaten"],
          placeholder: "Seit wann? In welchem Zimmer?",
          hint: "Wird meist noch am selben Tag beantwortet.",
          quickFixes: {
            0: {
              text: "Router 30 Sekunden vom Strom nehmen. Hilft in den meisten Fällen.",
            },
            1: {
              text: "Router 30 Sekunden vom Strom nehmen. Hilft in den meisten Fällen.",
            },
          },
        },
        {
          key: "amt",
          label: "Anmeldung & Rundfunkbeitrag",
          subs: [
            "Vermieterbescheinigung (Wohnungsgeberbestätigung)",
            "Rundfunkbeitrag",
            "Anmeldebestätigung",
          ],
          placeholder: "Was brauchst du?",
          hint: "Dokumente stehen meist sofort zum Download bereit.",
          quickFixes: {
            1: {
              text: "Den zahlen wir für die ganze Wohnung. Kommt Post vom Beitragsservice: auf rundfunkbeitrag.de „Wohnung ist bereits angemeldet“ wählen und die Beitragsnummer deiner Wohnung angeben.",
            },
          },
        },
        {
          key: "miete",
          label: "Miete & Kaution",
          subs: ["Zahlung", "Mietbescheinigung", "Kaution"],
          placeholder: "Worum geht es?",
          hint: "Antwort innerhalb von 2–3 Werktagen.",
          quickFixes: {
            0: {
              text: "Alle Abbuchungen und Belege findest du unter Mein Zimmer.",
              action: { kind: "tab", label: "Zu Mein Zimmer", tab: "room" },
            },
            2: {
              text: "Die Kaution zahlen wir nach der Abnahme beim Auszug zurück. Gesetzlich gilt eine angemessene Prüffrist, in der Regel bis zu sechs Monate.",
            },
          },
        },
        {
          key: "vertrag",
          label: "Vertrag",
          subs: ["Verlängerung", "Zimmerwechsel", "Kündigung"],
          placeholder: "Worum geht es?",
          hint: "Antwort innerhalb von 2–3 Werktagen.",
        },
        {
          key: "sonstiges",
          label: "Sonstiges",
          subs: [],
          placeholder: "Beschreibe kurz dein Anliegen.",
          hint: "Antwort innerhalb von 2–3 Werktagen.",
        },
      ]
    : [
        {
          key: "reparatur",
          label: "Repair",
          subs: [
            "Heating",
            "Water & bathroom",
            "Kitchen & appliances",
            "Furniture",
            "Electrics",
            "Windows & doors",
            "Keys & locks",
          ],
          placeholder: "What's broken and where exactly? Since when?",
          hint: "Photos help the tradesperson.",
          urgentSubs: [0, 6],
          quickFixes: {
            1: {
              text: "Drain blocked? Try a plunger first. If water is leaking, report it right away.",
            },
            2: {
              text: "Turn the appliance off and on again and check the filter. Usually helps.",
            },
            4: {
              text: "Fuse tripped? Check the fuse box and flip the switch back up.",
            },
          },
        },
        {
          key: "wg",
          label: "House rules & conflicts",
          subs: ["Noise", "House rules", "Safety"],
          placeholder: "What happened? Since when?",
          hint: "Confidential. Your flatmates won't see this.",
          quickFixes: {
            0: {
              text: "Best to raise it directly with your flatmates first. Quiet hours are 10 pm to 7 am. If it doesn't stop, we're here.",
            },
            1: {
              text: "It's all in the house rules. Often a word with your flatmates is enough.",
            },
          },
        },
        {
          key: "internet",
          label: "Internet",
          subs: ["No internet", "Slow", "Login details"],
          placeholder: "Since when? In which room?",
          hint: "Usually answered the same day.",
          quickFixes: {
            0: {
              text: "Unplug the router for 30 seconds. Fixes it in most cases.",
            },
            1: {
              text: "Unplug the router for 30 seconds. Fixes it in most cases.",
            },
          },
        },
        {
          key: "amt",
          label: "Registration & broadcasting fee",
          subs: [
            "Landlord confirmation (Wohnungsgeberbestätigung)",
            "Broadcasting fee",
            "Registration certificate",
          ],
          placeholder: "What do you need?",
          hint: "Documents are usually available to download instantly.",
          quickFixes: {
            1: {
              text: "We pay it for the whole flat. If you get a letter from the Beitragsservice: on rundfunkbeitrag.de choose “Wohnung ist bereits angemeldet” (flat already registered) and enter the fee number for your flat.",
            },
          },
        },
        {
          key: "miete",
          label: "Rent & deposit",
          subs: ["Payment", "Rent certificate", "Deposit"],
          placeholder: "What's it about?",
          hint: "Reply within 2–3 working days.",
          quickFixes: {
            0: {
              text: "All debits and receipts are under My room.",
              action: { kind: "tab", label: "Go to My room", tab: "room" },
            },
            2: {
              text: "We refund the deposit after the move-out inspection. By law a reasonable review period applies, usually up to six months.",
            },
          },
        },
        {
          key: "vertrag",
          label: "Contract",
          subs: ["Extension", "Room change", "Termination"],
          placeholder: "What's it about?",
          hint: "Reply within 2–3 working days.",
        },
        {
          key: "sonstiges",
          label: "Other",
          subs: [],
          placeholder: "Briefly describe your request.",
          hint: "Reply within 2–3 working days.",
        },
      ];
}

export function getApplicantHelpTopics(lang: "en" | "de"): HelpTopic[] {
  return lang === "de"
    ? [
        {
          key: "bewerbung",
          label: "Meine Bewerbung",
          subs: ["Status", "Dokumente", "Zurückziehen"],
          placeholder: "Worum geht es bei deiner Bewerbung?",
          hint: "Antwort innerhalb von 2–3 Werktagen.",
          quickFixes: {
            0: {
              text: "Den Stand jeder Bewerbung siehst du unter Bewerbungen. Wir melden uns zusätzlich per E-Mail.",
              action: {
                kind: "tab",
                label: "Zu Bewerbungen",
                tab: "applications",
              },
            },
            1: {
              text: "Fehlende Unterlagen lädst du direkt unter Bewerbungen hoch.",
              action: {
                kind: "tab",
                label: "Zu Bewerbungen",
                tab: "applications",
              },
            },
          },
        },
        {
          key: "besichtigung",
          label: "Besichtigung",
          subs: ["Termin", "Video-Besichtigung"],
          placeholder: "Welches Zimmer, und wann passt es dir?",
          hint: "Wir schlagen dir Termine vor.",
        },
        {
          key: "zimmer",
          label: "Fragen zum Zimmer",
          subs: ["Möblierung", "Mitbewohner", "Einzugsdatum"],
          placeholder: "Welches Zimmer, und was möchtest du wissen?",
          hint: "Antwort innerhalb von 2–3 Werktagen.",
        },
        {
          key: "kosten",
          label: "Kosten & Vertrag",
          subs: [
            "Was ist in der Miete enthalten?",
            "Kaution",
            "Mindestlaufzeit",
          ],
          placeholder: "Was möchtest du wissen?",
          hint: "Antwort innerhalb von 2–3 Werktagen.",
          quickFixes: {
            0: {
              text: "Die Warmmiete enthält Nebenkosten, Internet, Möbel und den Rundfunkbeitrag.",
            },
            1: {
              text: "Die Kaution steht in jedem Inserat und wird vor dem Einzug fällig. Zurück gibt es sie nach der Abnahme beim Auszug.",
            },
          },
        },
        {
          key: "sonstiges",
          label: "Sonstiges",
          subs: [],
          placeholder: "Beschreibe kurz dein Anliegen.",
          hint: "Antwort innerhalb von 2–3 Werktagen.",
        },
      ]
    : [
        {
          key: "bewerbung",
          label: "My application",
          subs: ["Status", "Documents", "Withdraw"],
          placeholder: "What's your question about your application?",
          hint: "Reply within 2–3 working days.",
          quickFixes: {
            0: {
              text: "You can see the status of each application under Applications. We'll also email you.",
              action: {
                kind: "tab",
                label: "Go to applications",
                tab: "applications",
              },
            },
            1: {
              text: "Upload missing documents directly under Applications.",
              action: {
                kind: "tab",
                label: "Go to applications",
                tab: "applications",
              },
            },
          },
        },
        {
          key: "besichtigung",
          label: "Viewing",
          subs: ["Appointment", "Video viewing"],
          placeholder: "Which room, and when suits you?",
          hint: "We'll suggest times.",
        },
        {
          key: "zimmer",
          label: "Questions about a room",
          subs: ["Furnishing", "Flatmates", "Move-in date"],
          placeholder: "Which room, and what would you like to know?",
          hint: "Reply within 2–3 working days.",
        },
        {
          key: "kosten",
          label: "Costs & contract",
          subs: ["What's included in the rent?", "Deposit", "Minimum term"],
          placeholder: "What would you like to know?",
          hint: "Reply within 2–3 working days.",
          quickFixes: {
            0: {
              text: "The all-in rent covers utilities, internet, furniture and the broadcasting fee.",
            },
            1: {
              text: "The deposit is listed in every ad and is due before move-in. You get it back after the move-out inspection.",
            },
          },
        },
        {
          key: "sonstiges",
          label: "Other",
          subs: [],
          placeholder: "Briefly describe your request.",
          hint: "Reply within 2–3 working days.",
        },
      ];
}

export function getTenantFaq(lang: "en" | "de"): FaqEntry[] {
  return lang === "de"
    ? [
        {
          id: "broadcastFee",
          q: "Muss ich den Rundfunkbeitrag bezahlen?",
          a: "Nein. coliviq zahlt den Beitrag für die ganze WG. Solltest du trotzdem einen Brief vom Beitragsservice bekommen, geh auf rundfunkbeitrag.de, wähle „Wohnung ist bereits angemeldet“ und gib die Beitragsnummer deiner Wohnung an. Fertig.",
        },
        {
          id: "registration",
          q: "Wie melde ich mich beim Bürgeramt an?",
          a: "Innerhalb von zwei Wochen nach dem Einzug. Vereinbare einen Termin beim Bürgeramt und bring deinen Ausweis sowie die Vermieterbescheinigung mit. Sie steht als PDF unter Mein Zimmer bereit.",
        },
        {
          id: "cleaning",
          q: "Wer putzt die Gemeinschaftsräume?",
          a: "Ihr als WG. Unter Community gibt es einen Putzplan, der sich jeden Montag dreht, damit klar ist, wer Küche, Bad, Flur und Mülltonnen diese Woche übernimmt.",
        },
        {
          id: "guests",
          q: "Dürfen Gäste übernachten?",
          a: "Ja. Sag deinen Mitbewohnern vorher Bescheid, am besten im WG-Chat. Details stehen in der Hausordnung.",
        },
        {
          id: "extendContract",
          q: "Kann ich meinen Vertrag verlängern?",
          a: "Ja, spätestens drei Monate vor Vertragsende. Schick uns einfach eine Anfrage unter „Vertrag“ oben.",
        },
        {
          id: "depositBack",
          q: "Wann bekomme ich meine Kaution zurück?",
          a: "Nach der Auszugsabnahme. Gesetzlich steht dem Vermieter eine angemessene Prüffrist zu, laut Rechtsprechung meist bis zu sechs Monate. Gibt es keine offenen Mängel, erstatten wir die Kaution, sobald die Prüfung abgeschlossen ist.",
        },
      ]
    : [
        {
          id: "broadcastFee",
          q: "Do I have to pay the broadcasting fee?",
          a: "No. coliviq pays the fee for the whole flat. If you still get a letter from the Beitragsservice, go to rundfunkbeitrag.de, choose “Wohnung ist bereits angemeldet” (flat already registered) and enter the fee number for your flat. Done.",
        },
        {
          id: "registration",
          q: "How do I register at the Bürgeramt?",
          a: "Within two weeks of moving in. Book an appointment at the Bürgeramt and bring your ID and the landlord confirmation. It's available as a PDF under My room.",
        },
        {
          id: "cleaning",
          q: "Who cleans the shared spaces?",
          a: "You do, as a flat. Under Community there's a cleaning rota that rotates every Monday, so everyone knows who covers kitchen, bathroom, hallway and bins this week.",
        },
        {
          id: "guests",
          q: "Can guests stay overnight?",
          a: "Yes. Let your flatmates know in the flat chat beforehand. Details are in the house rules.",
        },
        {
          id: "extendContract",
          q: "Can I extend my contract?",
          a: "Yes, at least three months before the contract ends. Just send a request under “Contract” above.",
        },
        {
          id: "depositBack",
          q: "When do I get my deposit back?",
          a: "After the move-out inspection. By law the landlord is entitled to a reasonable review period, usually up to six months according to case law. If there are no open defects, we refund it as soon as the review is complete.",
        },
      ];
}

export function getApplicantFaq(lang: "en" | "de"): FaqEntry[] {
  return lang === "de"
    ? [
        {
          id: "reviewTime",
          q: "Wie lange dauert die Prüfung?",
          a: "Normalerweise ein paar Werktage. Mit einem vollständigen Profil geht es schneller.",
        },
        {
          id: "documents",
          q: "Welche Unterlagen brauche ich?",
          a: "Ausweis und Einkommensnachweis. Du kannst pro Dokument mehrere Dateien hochladen. Studierende können statt Einkommen eine Immatrikulationsbescheinigung oder einen Bürgen angeben.",
        },
        {
          id: "multipleRooms",
          q: "Kann ich mich für mehrere Zimmer bewerben?",
          a: "Ja. Bekommst du eine Zusage, können wir deine anderen Bewerbungen auf Wunsch zurückziehen.",
        },
        {
          id: "rentIncludes",
          q: "Was ist in der Miete enthalten?",
          a: "Nebenkosten, Internet, Möbel und der Rundfunkbeitrag. Du zahlst einen festen Betrag im Monat.",
        },
        {
          id: "ownContract",
          q: "Bekomme ich einen eigenen Mietvertrag?",
          a: "Ja. Du mietest direkt von coliviq, keine Untervermietung.",
        },
      ]
    : [
        {
          id: "reviewTime",
          q: "How long does the review take?",
          a: "Usually a few working days. It's faster with a complete profile.",
        },
        {
          id: "documents",
          q: "Which documents do I need?",
          a: "ID and proof of income. You can upload several files per document. Students can provide an enrolment certificate or guarantor instead of income.",
        },
        {
          id: "multipleRooms",
          q: "Can I apply for several rooms?",
          a: "Yes. If you get an offer, we can withdraw your other applications on request.",
        },
        {
          id: "rentIncludes",
          q: "What's included in the rent?",
          a: "Utilities, internet, furniture and the broadcasting fee. You pay one fixed amount per month.",
        },
        {
          id: "ownContract",
          q: "Do I get my own rental contract?",
          a: "Yes. You rent directly from coliviq, no subletting.",
        },
      ];
}

export function getHelpScreenLabels(lang: "en" | "de") {
  return lang === "de"
    ? {
        reportIssueHeading: "Problem melden",
        reportIssueSub:
          "Wähle ein Thema und beschreibe kurz dein Anliegen. Wir melden uns.",
        topicLabel: "Thema",
        moreSpecificLabel: "Genauer",
        messageLabel: "Nachricht",
        addPhotos: "Fotos hinzufügen",
        send: "Senden",
        yourRequestsHeading: "Deine Anfragen",
        yourRequestsSub: "Hier siehst du, woran wir gerade arbeiten.",
        sendFailed:
          "Deine Anfrage konnte nicht gesendet werden. Bitte versuche es noch einmal.",
        nothingReported: "Noch nichts gemeldet.",
        faqHeading: "FAQ",
        quickFixHeading: "Vorher kurz probieren",
        thatHelped: "Hat geholfen",
        reportAnyway: "Trotzdem melden",
        helpedConfirmation: "Gut, dann ist nichts weiter zu tun.",
        morePhotos: "Weitere Fotos",
        removePhoto: "Foto entfernen",
        copied: "Kopiert",
        reportedOn: "Gemeldet am",
        statusInProgress: "In Bearbeitung",
        statusResolved: "Erledigt",
        /*
          The cap is written out rather than interpolated: translateDictionary
          only walks strings, so a template function here would skip
          translation entirely and ship English to every language. Keep the
          number in step with MAX_PHOTOS in TenantHelp.tsx.
        */
        photoLimitReached: "Du kannst bis zu 6 Fotos anhängen.",
      }
    : {
        reportIssueHeading: "Report an issue",
        reportIssueSub:
          "Pick a topic, briefly describe what's up. We'll get back to you.",
        topicLabel: "Topic",
        moreSpecificLabel: "More specific",
        messageLabel: "Message",
        addPhotos: "Add photos",
        send: "Send",
        yourRequestsHeading: "Your requests",
        yourRequestsSub: "Here you can see what we're working on.",
        sendFailed: "Your request could not be sent. Please try again.",
        nothingReported: "Nothing reported yet.",
        faqHeading: "FAQ",
        quickFixHeading: "Quick fix to try first",
        thatHelped: "That helped",
        reportAnyway: "Report anyway",
        helpedConfirmation: "Great, nothing else to do then.",
        morePhotos: "More photos",
        removePhoto: "Remove photo",
        copied: "Copied",
        reportedOn: "Reported on",
        statusInProgress: "In progress",
        statusResolved: "Resolved",
        photoLimitReached: "You can attach up to 6 photos.",
      };
}

/*
  Routing the nested copy above through the translation table (COL-477).

  translateDictionary covers flat strings and string arrays, which is all the
  LABELS need. These two helpers cover the two nested shapes it cannot: a list
  of {id,q,a} and a list of topics whose `subs` are themselves translatable.
  Deliberately local rather than a generalisation of translateDictionary --
  that function runs on every tenant surface, and widening it to walk arbitrary
  object arrays would put all of them at risk to fix this one screen.

  Keys come from the STABLE id/key, never the index or the text:
    tenant.help.faq.<id>.q           tenant.help.topic.<key>.label
    tenant.help.faq.<id>.a           tenant.help.topic.<key>.sub.<i>
                                     tenant.help.topic.<key>.placeholder
                                     tenant.help.topic.<key>.hint
                                     tenant.help.topic.<key>.quick.<i>.text
                                     tenant.help.topic.<key>.quick.<i>.action

  `subs` are indexed because they are plain strings with no id of their own, so
  a new sub-chip must be APPENDED -- inserting one mid-list re-points every row
  after it. urgentSubs indexes the same array and already imposes that rule.

  Tenant and applicant topics need SEPARATE namespaces: both sets contain a
  topic keyed "sonstiges" with different copy, so sharing one namespace would
  give an applicant the tenant's wording.
*/
export function translateFaq(
  namespace: string,
  entries: FaqEntry[],
  t: TranslateFn
): FaqEntry[] {
  return entries.map((entry) => ({
    ...entry,
    q: t(`${namespace}.${entry.id}.q`, entry.q),
    a: t(`${namespace}.${entry.id}.a`, entry.a),
  }));
}

export function translateHelpTopics(
  namespace: string,
  topics: HelpTopic[],
  t: TranslateFn
): HelpTopic[] {
  return topics.map((topic) => ({
    // Spread first: `key` and `urgentSubs` must survive untouched. urgentSubs
    // decides whether a report files as urgent and the admin queue triages on
    // it -- a translation pass must not be able to drop it.
    ...topic,
    label: t(`${namespace}.${topic.key}.label`, topic.label),
    placeholder: t(`${namespace}.${topic.key}.placeholder`, topic.placeholder),
    hint: t(`${namespace}.${topic.key}.hint`, topic.hint),
    subs: topic.subs.map((sub, i) =>
      t(`${namespace}.${topic.key}.sub.${i}`, sub)
    ),
    // Same index as the sub-chip it belongs to. `kind`, `tab` and `value`
    // are data and pass through; only the two visible strings translate.
    quickFixes: topic.quickFixes
      ? Object.fromEntries(
          Object.entries(topic.quickFixes).map(([i, fix]) => [
            i,
            {
              ...fix,
              text: t(`${namespace}.${topic.key}.quick.${i}.text`, fix.text),
              action: fix.action
                ? {
                    ...fix.action,
                    label: t(
                      `${namespace}.${topic.key}.quick.${i}.action`,
                      fix.action.label
                    ),
                  }
                : undefined,
            },
          ])
        )
      : undefined,
  }));
}
